<template>
  <p class="block-img">
    <a v-if="href" :href="href" :target="linkTarget" :rel="linkRel">
      <img
        :src="defaultSrc"
        :alt="altText"
        :copyright="copyrightAttr"
        :width="w"
        :height="h || undefined"
        :srcset="srcset || undefined"
        :sizes="srcset ? '(max-width: 767px) 100vw, 424px' : undefined"
        loading="lazy"
        decoding="async"
      />
    </a>
    <img
      v-else
      :src="defaultSrc"
      :alt="altText"
      :copyright="copyrightAttr"
      :width="w"
      :height="h || undefined"
      :srcset="srcset || undefined"
      :sizes="srcset ? '(max-width: 767px) 100vw, 424px' : undefined"
      loading="lazy"
      decoding="async"
    />
  </p>
</template>

<script>
import { computed } from "vue";
import { getRichTextComponentProps } from "@prismicio/vue";
import { asLink } from "@prismicio/client";
import { linkResolver } from "./linkResolver";

const MAX_WIDTH = 800;
const SRCSET_WIDTHS = [400, 600, 800];

const buildUrl = (baseUrl, width) => {
  const u = new URL(baseUrl);
  u.searchParams.set("w", String(width));
  u.searchParams.set("auto", "compress,format");
  return u.href;
};

export default {
  name: "PrismicImageBlock",
  props: getRichTextComponentProps("image"),
  setup(props) {
    const url = computed(() => props.node.url);
    const dimensions = computed(() => props.node.dimensions);
    const linkTo = computed(() => props.node.linkTo);

    const srcset = computed(() => {
      const widths = SRCSET_WIDTHS.filter(
        (sw) => !dimensions.value?.width || sw <= dimensions.value.width,
      );
      return widths.map((sw) => `${buildUrl(url.value, sw)} ${sw}w`).join(", ");
    });

    const defaultSrc = computed(() => buildUrl(url.value, SRCSET_WIDTHS[0]));

    const w = computed(() =>
      dimensions.value?.width
        ? Math.min(dimensions.value.width, MAX_WIDTH)
        : MAX_WIDTH,
    );

    const h = computed(() => {
      const d = dimensions.value;
      if (d?.height && d?.width) {
        return Math.round((w.value / d.width) * d.height);
      }
      return null;
    });

    const altText = computed(() => props.node.alt || "");
    const copyrightAttr = computed(() => props.node.copyright || undefined);

    const href = computed(() =>
      linkTo.value ? asLink(linkTo.value, { linkResolver }) || "" : null,
    );
    const linkTarget = computed(() => linkTo.value?.target || undefined);
    const linkRel = computed(() =>
      linkTo.value?.target ? "noopener noreferrer" : undefined,
    );

    return {
      defaultSrc,
      srcset,
      w,
      h,
      altText,
      copyrightAttr,
      href,
      linkTarget,
      linkRel,
    };
  },
};
</script>
